import config from 'constants/config'
import FilterPanelLoader from 'components/Common/FilterPanel/FilterPanelCheckboxLoader'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { arrayToObject } from 'lib/array/arrayUtils'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import useMostPopulatedSearchExperienceFacets from '../hooks/useMostPopulatedSearchExperienceFacets'
import ExperienceSearchDateFilter from './ExperienceSearchDateFilter'
import ExperienceSearchFeaturedFilter from './ExperienceSearchFeaturedFilter'
import ExperienceSearchRatingFilter from './ExperienceSearchRatingFilter'
import ExperienceSearchPriceFilter from './ExperienceSearchPriceFilter'
import ExperienceSearchOnlyAtLuxuryEscapesFilter from './ExperienceSearchOnlyAtLuxuryEscapesFilter'

interface Props {
  place?: App.Place;
  listIds?: Array<string>;
}

function ExperienceSearchPageFilters({ place, listIds }: Props) {
  const intl = useIntl()
  const {
    fetching,
    ratingCounts,
    freeCancellationCount,
    instantConfirmationCount,
    giftingAvailableCount,
  } = useMostPopulatedSearchExperienceFacets(place)

  const ratingCountsRecord = useMemo<Record<number, number>>(() => {
    return arrayToObject(
      ratingCounts,
      (item) => item.threshold,
      (item) => item.count,
    )
  }, [ratingCounts])

  const isLuxuryEscapesBrand = config.BRAND === 'luxuryescapes'

  const featuredCounts = useMemo<Record<Experiences.FeaturedFilter, number>>(
    () => ({
      'free-cancellation': freeCancellationCount,
      'instant-confirmation': instantConfirmationCount,
      'is-gift': giftingAvailableCount,
    }),
    [freeCancellationCount, instantConfirmationCount, giftingAvailableCount],
  )

  if (fetching) {
    return (
      <VerticalSpacer gap={16}>
        <FilterPanelLoader type="checkbox" />
        <FilterPanelLoader type="slider" />
        {isLuxuryEscapesBrand && <FilterPanelLoader type="checkbox" />}
        <FilterPanelLoader type="checkbox" />
        <FilterPanelLoader type="checkbox" />
      </VerticalSpacer>
    )
  }

  return (
    <VerticalSpacer gap={16}>
      <FilterPanelFilterGroup
        title={intl.formatMessage({
          id: '-WRRPuOIUghkYN7lsPFm',
          defaultMessage: 'Date',
          description:
            'Form field label for the date field of a tour experience',
        })}
      >
        <ExperienceSearchDateFilter />
      </FilterPanelFilterGroup>
      <FilterPanelFilterGroup
        title={intl.formatMessage({
          id: 'uhzheYPeu42wS_U04j7E',
          defaultMessage: 'Price',
          description:
            'Title for the price filter group in experience search filters',
        })}
      >
        <ExperienceSearchPriceFilter place={place} listIds={listIds} />
      </FilterPanelFilterGroup>
      {isLuxuryEscapesBrand && (
        <FilterPanelFilterGroup
          title={intl.formatMessage({
            id: 'n8IWE-Sw_MeTWE6gGRRQ',
            defaultMessage: 'Only at Luxury Escapes',
            description:
              'Title for the Luxury Escapes exclusive filter group in experience search filters',
          })}
        >
          <ExperienceSearchOnlyAtLuxuryEscapesFilter place={place} />
        </FilterPanelFilterGroup>
      )}
      <FilterPanelFilterGroup
        title={intl.formatMessage({
          id: '9eLIu9TMkOWSCYPks6rx',
          defaultMessage: 'Featured',
          description:
            'Title for the featured filter group in experience search filters',
        })}
      >
        <ExperienceSearchFeaturedFilter featuredCounts={featuredCounts} />
      </FilterPanelFilterGroup>
      <FilterPanelFilterGroup
        title={intl.formatMessage({
          id: 'XGkSHbOIT8muMeKg7j0c',
          defaultMessage: 'Customer rating',
          description:
            'Title for the rating filter group in experience search filters',
        })}
      >
        <ExperienceSearchRatingFilter ratingCounts={ratingCountsRecord} />
      </FilterPanelFilterGroup>
    </VerticalSpacer>
  )
}

export default ExperienceSearchPageFilters
